<template lang="pug">
system-tabs(section="/system/team")
    template(slot="tabbar-buttons")
        //- Convention, left to right and copied from the account form: the
        //- destructive action first and set apart, then the way out, then
        //- save-and-close in primary blue, then save in success green with the
        //- disk icon. Nothing on the page below writes on its own.
        el-tooltip(v-if="saveState.canDemote && !saveState.loadError" effect="dark" :content="$t('Remove super user')" placement="bottom")
            el-button.delete-button(
                circle
                type="danger"
                icon="el-icon-fa-user-slash fas"
                :loading="saveState.demoting"
                @click="demoteVisible = true"
            )
        el-button(circle icon="el-icon-fa-times fas" @click="dismiss")
        el-button(
            v-if="!saveState.loadError"
            circle
            type="primary"
            icon="el-icon-fa-check fas"
            :loading="savingAndClose"
            @click="saveAndClose"
        )
        el-button(
            v-if="!saveState.loadError"
            circle
            type="success"
            icon="el-icon-fa-save fas"
            :loading="saveState.saving"
            @click="save"
        )
    el-card(shadow="always")
        team-detail(ref="detail" :user-id="userId" scope="system" @state="saveState = $event")
    el-dialog.remove-super-user-dialog(
        :title="$t('Remove super user')"
        :visible.sync="demoteVisible"
        width="480px"
        append-to-body
    )
        p.remove-super-user-dialog__lead {{ $t('{name} loses the platform area, every platform right and any restriction to specific websites.', { name: demoteName }) }}
        p.help {{ $t('Their rights on the websites they administer stay. They can be added back at any time.') }}
        //- Same footer every other dialog here uses: not round, icon + label,
        //- Element's own spacing between them.
        template(slot="footer")
            el-button(
                size="mini"
                icon="el-icon-fa-times fas"
                @click="demoteVisible = false"
            ) {{ $t('Dismiss') }}
            el-button(
                size="mini"
                type="danger"
                icon="el-icon-fa-user-slash fas"
                :loading="saveState.demoting"
                @click="confirmDemote"
            ) {{ $t('Remove super user') }}

    .buttons-flex
        .form-buttons
        .form-buttons
            el-button(round icon="el-icon-fa-times fas" @click="dismiss") {{ $t('Dismiss') }}
            el-button(
                v-if="!saveState.loadError"
                round
                type="primary"
                icon="el-icon-fa-check fas"
                :loading="savingAndClose"
                @click="saveAndClose"
            ) {{ $t('Save and close') }}
            el-button(
                v-if="!saveState.loadError"
                round
                type="success"
                icon="el-icon-fa-save fas"
                :loading="saveState.saving"
                @click="save"
            ) {{ $t('Save') }}
</template>

<i18n>
{
    "en": {
        "{name} loses the platform area, every platform right and any restriction to specific websites.": "{name} loses the platform area, every platform right and any restriction to specific websites."
    },
    "de": {
        "Dismiss": "Verwerfen",
        "This person": "Diese Person",
        "Nothing to save.": "Nichts zu speichern.",
        "No change has been made yet.": "Es wurde noch nichts geändert.",
        "Remove super user": "Super User entziehen",
        "{name} loses the platform area, every platform right and any restriction to specific websites.": "{name} verliert den Plattform-Bereich, alle Plattform-Rechte und eine Beschränkung auf einzelne Webauftritte.",
        "Their rights on the websites they administer stay. They can be added back at any time.": "Die Rechte auf den betreuten Webauftritten bleiben bestehen. Die Person kann jederzeit wieder aufgenommen werden.",
        "Save": "Speichern",
        "Save and close": "Speichern und schließen"
    }
}
</i18n>

<script>
import SystemTabs from '@/views/system/Partials/Tabs';
import TeamDetail from '@/views/system/Team/Detail';

/**
 * Chrome only, mirroring SystemProjectDetail: a plain card (NOT
 * `.contains-table`, which is for list panels) and the controls in both places
 * the admin puts them — the tab bar and the row under the card.
 *
 * The detail emits its dirty/saving state; this decides when Save is offered
 * and calls back into it. No section on the page carries a Save of its own.
 */
export default {
    name: 'SystemTeamDetail',
    components: {
        SystemTabs,
        'team-detail': TeamDetail,
    },
    data() {
        return {
            saveState: {},
            savingAndClose: false,
            demoteVisible: false,
        };
    },
    computed: {
        userId() {
            return this.$route.params.id;
        },
        // The detail reports the name once it has loaded the person. Until
        // then the sentence still has to read as a sentence.
        demoteName() {
            return this.saveState.memberName || this.$t('This person');
        },
    },
    methods: {
        callDetail(method, ...args) {
            const detail = this.$refs.detail;
            if (detail) detail[method](...args);
        },
        // The buttons are never greyed out: a dead control tells nobody why it
        // is dead. Clicking Save with nothing changed says so.
        sayNothingToSave() {
            this.$notify.info({
                title: this.$t('Nothing to save.'),
                message: this.$t('No change has been made yet.'),
                iconClass: 'fas el-icon-fa-circle-info',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
        save() {
            if (!this.saveState.dirty) return this.sayNothingToSave();

            return this.callDetail('save');
        },
        confirmDemote() {
            const detail = this.$refs.detail;

            if (!detail) return;

            Promise.resolve(detail.demote()).then((removed) => {
                this.demoteVisible = false;

                // They are no longer on the team, so the page they are on no
                // longer has anything to show.
                if (removed) this.dismiss();
            });
        },
        saveAndClose() {
            const detail = this.$refs.detail;

            if (!detail) return;

            if (!this.saveState.dirty) return this.sayNothingToSave();

            this.savingAndClose = true;
            Promise.resolve(detail.save())
                .then(() => this.dismiss())
                .finally(() => {
                    this.savingAndClose = false;
                });
        },
        dismiss() {
            this.$router.push({ name: 'system-team' });
        },
    },
};
</script>

<style lang="sass" scoped>
// Element's dialog body sets no type scale, so a bare <p> inherits the page
// size and the browser's own margins - two paragraphs of near-identical
// weight running into each other.
.remove-super-user-dialog
    .remove-super-user-dialog__lead
        margin: 0
        font-size: 13px
        line-height: 1.5
        color: #303133
    .help
        margin: 8px 0 0
        font-size: 12px
        line-height: 1.4
        color: #909399
</style>
